import React, { useMemo, useCallback } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Position,
  Handle,
  MarkerType,
  useNodesState,
  useEdgesState,
  NodeProps,
  Controls,
  Background,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Box, Typography, Stack } from '@mui/material';
import { Key as KeyIcon, Link as FKIcon } from '@mui/icons-material';
import { getLayoutedElements } from '../canvas/layoutUtils';

// ── Local types (kept internal so component stays self-contained) ──

interface ColumnSummary {
  name: string;
  semantic_role: string;
  description: string;
  pii_likelihood: number;
  is_primary_key?: boolean;
  is_foreign_key?: boolean;
  fk_references?: string;
  data_type?: string;
}

interface JoinDefinition {
  on: string[];
  to: string;
  join_type?: string;
  confidence?: number;
  description?: string;
}

interface TableSummary {
  file_name: string;
  original_name?: string;
  semantic_name: string;
  description: string;
  entity_type: string;
  primary_key_candidates?: string[];
  foreign_key_candidates?: string[];
  likely_joins?: JoinDefinition[];
  column_summaries: ColumnSummary[];
}

// ── Node data ──

interface ERNodeData {
  label: string;
  pkColumns: string[];
  fkColumns: string[];
  allColumns: string[];
  columnCount: number;
  entityType: string;
  tableIndex: number;
  isFocused: boolean;    // Is this the currently-viewed table?
  showAllColumns: boolean; // Show full column list (focused mode)
}

// ── Props ──

interface RelationshipDiagramProps {
  tables: TableSummary[];
  onTableClick?: (tableIndex: number) => void;
  height?: number;
  /** When set, only shows this table + its directly related tables, with columns listed.
   *  The value should be the table's file_name. */
  focusedTableId?: string;
}

// ── Compact node for top-level (full ER) view ──

const ERTableNode: React.FC<NodeProps<ERNodeData>> = ({ data }) => (
  <Box
    sx={{
      border: '2px solid',
      borderColor: data.isFocused ? '#e65100' : '#1565c0',
      borderRadius: 1,
      bgcolor: 'background.paper',
      minWidth: 180,
      boxShadow: data.isFocused ? 4 : 1,
      overflow: 'hidden',
      cursor: 'pointer',
    }}
  >
    <Handle type="target" position={Position.Left} style={{ background: '#64748b' }} />
    {/* Header */}
    <Box
      sx={{
        px: 1.5,
        py: 0.75,
        bgcolor: data.isFocused ? '#fff3e0' : '#e3f2fd',
        borderBottom: '1px solid',
        borderColor: data.isFocused ? '#ffcc80' : '#90caf9',
      }}
    >
      <Typography
        variant="subtitle2"
        noWrap
        sx={{
          fontSize: '0.85rem',
          fontWeight: 700,
          color: data.isFocused ? '#e65100' : '#0d47a1',
        }}
      >
        {data.label}
      </Typography>
      {data.entityType && data.entityType !== 'unknown' && (
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.7rem',
            color: data.isFocused ? '#bf360c' : '#1565c0',
          }}
        >
          {data.entityType}
        </Typography>
      )}
    </Box>
    {/* Columns body */}
    <Box sx={{ px: 1.5, py: 0.5 }}>
      {data.showAllColumns ? (
        // Focused mode: show every column with PK/FK icons
        <>
          {data.allColumns.map((col) => {
            const isPK = data.pkColumns.includes(col);
            const isFK = data.fkColumns.includes(col);
            return (
              <Stack
                key={col}
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ py: 0.1 }}
              >
                {isPK && <KeyIcon sx={{ fontSize: 13, color: 'success.main' }} />}
                {isFK && <FKIcon sx={{ fontSize: 13, color: 'info.main' }} />}
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: isPK ? 600 : 400,
                    color: isPK ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {col}
                </Typography>
              </Stack>
            );
          })}
        </>
      ) : (
        // Compact mode: PK columns + count
        <>
          {data.pkColumns.map((pk) => (
            <Stack key={pk} direction="row" spacing={0.5} alignItems="center">
              <KeyIcon sx={{ fontSize: 13, color: 'success.main' }} />
              <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {pk}
              </Typography>
            </Stack>
          ))}
          {data.columnCount > data.pkColumns.length && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              +{data.columnCount - data.pkColumns.length} columns
            </Typography>
          )}
        </>
      )}
    </Box>
    <Handle type="source" position={Position.Right} style={{ background: '#64748b' }} />
  </Box>
);

const nodeTypes = { erTable: ERTableNode };

// ── Helpers ──

/** Build the set of table file_names directly related to `focusedId` via likely_joins. */
function getRelatedTableIds(tables: TableSummary[], focusedId: string): Set<string> {
  const related = new Set<string>([focusedId]);
  const focusedTable = tables.find((t) => t.file_name === focusedId);
  if (focusedTable) {
    for (const join of focusedTable.likely_joins || []) {
      if (join.to) related.add(join.to);
    }
  }
  // Also include tables that join TO the focused table
  for (const t of tables) {
    for (const join of t.likely_joins || []) {
      if (join.to === focusedId) related.add(t.file_name);
    }
  }
  return related;
}

// ── Component ──

const RelationshipDiagram: React.FC<RelationshipDiagramProps> = ({
  tables,
  onTableClick,
  height = 300,
  focusedTableId,
}) => {
  const isFocusedMode = !!focusedTableId;

  const { initialNodes, initialEdges } = useMemo(() => {
    // In focused mode, only show related tables
    const visibleIds = isFocusedMode
      ? getRelatedTableIds(tables, focusedTableId!)
      : new Set(tables.map((t) => t.file_name));

    const visibleTables = tables.filter((t) => visibleIds.has(t.file_name));
    const visibleNames = new Set(visibleTables.map((t) => t.file_name));

    // Build nodes
    const nodes: Node<ERNodeData>[] = visibleTables.map((table) => {
      const originalIndex = tables.findIndex((t) => t.file_name === table.file_name);
      const showAllCols = isFocusedMode;
      const pkCols = table.primary_key_candidates || [];
      const fkCols = table.foreign_key_candidates || [];
      const allCols = (table.column_summaries || []).map((c) => c.name);
      const colCount = allCols.length;

      // Calculate node height based on content
      const headerHeight = 48;
      const colLineHeight = 20;
      const bodyPadding = 8;
      const bodyHeight = showAllCols
        ? colCount * colLineHeight + bodyPadding
        : Math.min(pkCols.length, 4) * colLineHeight + (colCount > pkCols.length ? 18 : 0) + bodyPadding;
      const nodeHeight = headerHeight + bodyHeight;
      const nodeWidth = showAllCols ? 200 : 185;

      return {
        id: table.file_name,
        type: 'erTable',
        data: {
          label: table.semantic_name || table.file_name,
          pkColumns: pkCols,
          fkColumns: fkCols,
          allColumns: allCols,
          columnCount: colCount,
          entityType: table.entity_type || 'unknown',
          tableIndex: originalIndex,
          isFocused: table.file_name === focusedTableId,
          showAllColumns: showAllCols,
        },
        position: { x: 0, y: 0 },
        width: nodeWidth,
        height: nodeHeight,
      };
    });

    // Build edges (only between visible tables)
    const edges: Edge[] = [];
    const seenEdges = new Set<string>();

    visibleTables.forEach((table) => {
      (table.likely_joins || []).forEach((join, idx) => {
        const target = join.to || '';
        if (!target || !visibleNames.has(target)) return;

        const edgeKey = [table.file_name, target].sort().join('--');
        if (seenEdges.has(edgeKey)) return;
        seenEdges.add(edgeKey);

        const conf = join.confidence || 0.5;
        const isHighConf = conf > 0.7;
        const isFK = join.join_type === 'fk_candidate' || join.join_type === 'pk_candidate';

        edges.push({
          id: `${table.file_name}-${target}-${idx}`,
          source: table.file_name,
          target: target,
          label: join.on.join(', '),
          style: {
            stroke: isFK ? '#3b82f6' : '#94a3b8',
            strokeWidth: isHighConf ? 2.5 : 1.5,
            strokeDasharray: isHighConf ? undefined : '6 4',
          },
          labelStyle: {
            fontSize: 12,
            fontWeight: 600,
            fill: '#334155',
          },
          labelBgStyle: {
            fill: '#ffffff',
            fillOpacity: 0.95,
            stroke: '#cbd5e1',
            strokeWidth: 1,
          },
          labelBgPadding: [6, 4] as [number, number],
          markerEnd: { type: MarkerType.ArrowClosed, color: isFK ? '#3b82f6' : '#94a3b8' },
        });
      });
    });

    if (nodes.length > 0 && edges.length > 0) {
      const layouted = getLayoutedElements(nodes, edges, 'LR');
      return { initialNodes: layouted.nodes, initialEdges: layouted.edges };
    }

    // If no edges, lay out nodes in a row
    const spacedNodes = nodes.map((n, i) => ({
      ...n,
      position: { x: i * 240, y: 0 },
    }));
    return { initialNodes: spacedNodes, initialEdges: edges };
  }, [tables, focusedTableId, isFocusedMode]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Sync when tables or focus change
  React.useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node<ERNodeData>) => {
      if (onTableClick) {
        onTableClick(node.data.tableIndex);
      }
    },
    [onTableClick]
  );

  if (tables.length < 2 && !focusedTableId) return null;

  return (
    <Box
      sx={{
        height,
        width: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        minZoom={0.3}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
        nodesDraggable
        nodesConnectable={false}
        elementsSelectable
      >
        <Background gap={16} size={1} color="#f1f5f9" />
        <Controls showInteractive={false} />
      </ReactFlow>
    </Box>
  );
};

export default RelationshipDiagram;
